import React, { useState, useEffect, useRef } from 'react';
import cl from './Course.module.css';
import { useParams, Link } from 'react-router-dom';
import AdminService from '../../API/AdminService';
import Spinner from '../../components/UI/Spinner/Spinner';
import logger from '../../utils/logger';
import {
    IconArrowLeft, IconJournal, IconClock, IconCalendar,
    IconUser, IconStudents,
} from '../../components/Icons';

const Course = () => {
    const { id } = useParams();

    // Состояние данных
    const [course, setCourse] = useState(null);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dates, setDates] = useState([]);

    // Состояние интерфейса
    const [saving, setSaving] = useState(false);

    // Загрузка данных при монтировании компонента
    useEffect(() => {
        if (id) {
            loadCourseData();
        }
    }, [id]);

    // Загрузка данных курса и студентов
    const loadCourseData = async () => {
        setLoading(true);
        setError('');
        try {
            // Загружаем курс
            const courseResponse = await AdminService.getCourse(id);
            const courseData = courseResponse.data;
            setCourse(courseData);

            // Загружаем всех студентов
            const studentsResponse = await AdminService.getAllStudents();
            const allStudents = studentsResponse.data || [];

            // Фильтруем студентов, записанных на этот курс
            const courseStudents = allStudents.filter(student => {
                try {
                    const groups = JSON.parse(student.group || '[]');
                    return groups.includes(parseInt(id));
                } catch (e) {
                    return false;
                }
            });
            setStudents(courseStudents);

            // Генерируем даты курса
            if (courseData.dateStart && courseData.dateEnd) {
                const generatedDates = generateCourseDates(courseData);
                setDates(generatedDates);
            }

        } catch (error) {
            logger.error('Ошибка загрузки данных:', error);
            setError('Ошибка загрузки данных курса');
        } finally {
            setLoading(false);
        }
    };

    // Генерация дат курса с учетом дня недели
    const generateCourseDates = (courseData) => {
        const startDate = new Date(courseData.dateStart);
        const endDate = new Date(courseData.dateEnd);
        const dayOfWeek = courseData.day; // 0-воскресенье, 1-понедельник и т.д.
        const dates = [];

        // Найти первую дату курса
        let currentDate = new Date(startDate);

        // Перемещаемся к первому дню недели курса
        while (currentDate.getDay() !== dayOfWeek && currentDate <= endDate) {
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Генерируем все даты курса
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 7); // Следующая неделя
        }

        return dates;
    };

    // Форматирование даты для отображения (только число)
    const formatDateNumber = (date) => {
        return date.getDate().toString().padStart(2, '0');
    };

    // Форматирование месяца для отображения
    const formatMonth = (date) => {
        return (date.getMonth() + 1).toString().padStart(2, '0');
    };

    // Форматирование даты для сохранения (YYYY-MM-DD).
    // ВАЖНО: используем локальные год/месяц/день, а не toISOString(),
    // потому что toISOString() возвращает UTC. На GMT+3 это даёт дату
    // на 1 день раньше для дат с временем 00:00:00 МСК — поэтому в
    // журналах ничего не отображалось (БД пишет '2025-09-06', а фронт
    // искал '2025-09-05').
    const formatDateForSave = (date) => {
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Получение журнала студента для курса
    const getStudentJournal = (student) => {
        try {
            const journal = JSON.parse(student.journal || '{}');
            return journal[id] || { visit: [], pay: [] };
        } catch (e) {
            return { visit: [], pay: [] };
        }
    };

    // Проверка посещения
    const isVisited = (student, date) => {
        const journal = getStudentJournal(student);
        const dateString = formatDateForSave(date);
        return journal.visit?.includes(dateString) || false;
    };

    // Проверка оплаты
    const isPaid = (student, date) => {
        const journal = getStudentJournal(student);
        const dateString = formatDateForSave(date);
        return journal.pay?.includes(dateString) || false;
    };

    // Обновление журнала студента
    const updateStudentJournal = async (student, date, type) => {
        setSaving(true);
        try {
            const journal = JSON.parse(student.journal || '{}');
            const courseJournal = journal[id] || { visit: [], pay: [] };
            const dateString = formatDateForSave(date);

            // Переключаем состояние
            const currentArray = courseJournal[type] || [];
            const isCurrentlyChecked = currentArray.includes(dateString);

            if (isCurrentlyChecked) {
                // Убираем отметку
                courseJournal[type] = currentArray.filter(d => d !== dateString);
            } else {
                // Добавляем отметку
                if (!currentArray.includes(dateString)) {
                    courseJournal[type] = [...currentArray, dateString];
                }
            }

            journal[id] = courseJournal;

            // Отправляем обновление на сервер
            await AdminService.updateStudent(student.id, {
                journal: JSON.stringify(journal)
            });

            // Обновляем локальное состояние
            setStudents(prevStudents =>
                prevStudents.map(s =>
                    s.id === student.id
                        ? { ...s, journal: JSON.stringify(journal) }
                        : s
                )
            );

        } catch (error) {
            logger.error('Ошибка обновления журнала:', error);
            alert('Ошибка сохранения данных');
        } finally {
            setSaving(false);
        }
    };

    // Обработка клика на посещение
    const handleVisitClick = (student, date, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (saving) return;
        updateStudentJournal(student, date, 'visit');
    };

    // Обработка клика на оплату (правая кнопка мыши, Ctrl+клик или
    // долгое нажатие на тач-устройствах).
    const handlePayClick = (student, date, e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        if (saving) return;
        updateStudentJournal(student, date, 'pay');
    };

    // Обработка клика на ячейку
    const handleCellClick = (student, date, e) => {
        // Если только что был long-press — не запускаем «обычный клик»
        if (longPressedRef.current) {
            longPressedRef.current = false;
            return;
        }
        if (e.ctrlKey || e.metaKey) {
            // Ctrl+клик = оплата
            handlePayClick(student, date, e);
        } else {
            // Обычный клик = посещение
            handleVisitClick(student, date, e);
        }
    };

    // Long-press на тач-устройствах: удержание ячейки ≈500мс отмечает оплату
    // вместо посещения. Реализовано через таймер: touchstart запускает,
    // touchend / touchmove / touchcancel отменяют.
    const longPressTimerRef = useRef(null);
    const longPressedRef = useRef(false);

    const handleTouchStart = (student, date) => {
        longPressedRef.current = false;
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = setTimeout(() => {
            longPressedRef.current = true;
            // Лёгкая тактильная отдача, если устройство умеет
            if (navigator.vibrate) {
                try { navigator.vibrate(15); } catch { /* ignore */ }
            }
            handlePayClick(student, date, null);
        }, 500);
    };
    const cancelLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    // Получение CSS класса для ячейки
    const getCellClass = (student, date) => {
        const visited = isVisited(student, date);
        const paid = isPaid(student, date);

        let classes = [cl.journalCell];

        if (paid) {
            classes.push(cl.cellPaid);
        }

        if (visited) {
            classes.push(cl.cellVisited);
        }

        return classes.join(' ');
    };

    // Содержимое ячейки журнала: галочка, если посетил, иначе пусто.
    // Оставлен текстовый символ — старые CSS-правила ячейки ожидают строку.
    const getCellContent = (student, date) => {
        const visited = isVisited(student, date);
        return visited ? '✓' : '';
    };

    // Дни недели
    const daysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <Spinner size="large" text="Загрузка журнала" fullPage />
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.error}>{error}</div>
                </div>
            </div>
        );
    }

    if (!course) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.error}>Курс не найден</div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Назад */}
                <Link to="/main/jornal" className={cl.backLink}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К расписанию</span>
                </Link>

                {/* Карточка-шапка курса.
                    Слева — иконка журнала + название курса + микро-метрики.
                    Справа — статистика учеников/занятий и индикатор сохранения. */}
                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconJournal width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <h1 className={cl.pageHeaderTitle}>{course.name}</h1>
                            <div className={cl.metaRow}>
                                <span className={cl.metaItem}>
                                    <IconCalendar width={13} height={13}/>
                                    {daysOfWeek[course.day]}
                                </span>
                                <span className={cl.metaItem}>
                                    <IconClock width={13} height={13}/>
                                    {course.timeStart} – {course.timeEnd}
                                </span>
                                {course.prepod && (
                                    <span className={cl.metaItem}>
                                        <IconUser width={13} height={13}/>
                                        {course.prepod}
                                    </span>
                                )}
                                <span className={cl.metaItemMuted}>
                                    {course.dateStart} – {course.dateEnd}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderRight}>
                        <div className={cl.statPill}>
                            <IconStudents width={14} height={14}/>
                            <span className={cl.statPillNum}>{students.length}</span>
                            <span className={cl.statPillLabel}>учеников</span>
                        </div>
                        <div className={cl.statPill}>
                            <IconCalendar width={14} height={14}/>
                            <span className={cl.statPillNum}>{dates.length}</span>
                            <span className={cl.statPillLabel}>занятий</span>
                        </div>
                        {saving && (
                            <div className={cl.savingPill}>
                                <span className={cl.savingDot}/>
                                Сохранение…
                            </div>
                        )}
                    </div>
                </div>



                {/* Таблица журнала */}
                {students.length === 0 ? (
                    <div className={cl.noStudents}>
                        На этот курс не записано ни одного ученика
                    </div>
                ) : (
                    <div className={cl.journalContainer}>
                        <div className={cl.journalTable}>
                            {/* Заголовок таблицы */}
                            <div className={cl.tableHeader}>
                                <div className={cl.headerCell}>
                                    <div className={cl.studentHeader}>Ученик</div>
                                </div>
                                <div className={cl.datesHeader}>
                                    {dates.map((date, index) => (
                                        <div key={index} className={cl.dateCell}>
                                            <div className={cl.dateMonth}>{formatMonth(date)}</div>
                                            <div className={cl.dateNumber}>{formatDateNumber(date)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Строки студентов */}
                            <div className={cl.tableBody}>
                                {students.map((student) => (
                                    <div key={student.id} className={cl.studentRow}>
                                        {/* Имя студента (зафиксированный столбец).
                                            Клик ведёт на страницу профиля ученика. */}
                                        <Link
                                            to={`/main/students/${student.id}`}
                                            className={cl.studentCell}
                                            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                            title="Открыть профиль ученика"
                                        >
                                            <div className={cl.studentName}>
                                                {student.ferstName} {student.lastName}
                                            </div>
                                            <div className={cl.studentAge}>
                                                {student.age} лет
                                            </div>
                                        </Link>

                                        {/* Ячейки для каждой даты */}
                                        <div className={cl.datesRow}>
                                            {dates.map((date, dateIndex) => (
                                                <div
                                                    key={dateIndex}
                                                    className={getCellClass(student, date)}
                                                    onClick={(e) => handleCellClick(student, date, e)}
                                                    onContextMenu={(e) => handlePayClick(student, date, e)}
                                                    onTouchStart={() => handleTouchStart(student, date)}
                                                    onTouchEnd={cancelLongPress}
                                                    onTouchMove={cancelLongPress}
                                                    onTouchCancel={cancelLongPress}
                                                    title={`${student.ferstName} ${student.lastName} - ${formatMonth(date)}/${formatDateNumber(date)}`}
                                                >
                                                    {getCellContent(student, date)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Подсказка-карточка с легендой и инструкциями */}
                <div className={cl.hintCard}>
                    <div className={cl.hintLeft}>
                        <h3 className={cl.hintTitle}>Как пользоваться</h3>
                        <ul className={cl.hintList}>
                            <li><strong>Клик</strong> — отметить или снять посещение.</li>
                            <li><strong>Ctrl + клик</strong> — отметить или снять оплату.</li>
                            <li><strong>Долгое нажатие</strong> на мобильном — отметить или снять оплату.</li>
                        </ul>
                    </div>
                    <div className={cl.hintRight}>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellVisited}`}>✓</span>
                            <span>Присутствовал</span>
                        </div>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellPaid}`}/>
                            <span>Оплачено</span>
                        </div>
                        <div className={cl.legendItem}>
                            <span className={`${cl.legendSquare} ${cl.cellDefault}`}/>
                            <span>Пусто</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Course;